import { Box, Typography, Paper, Grid, Tooltip } from "@mui/material";
import { useState, useRef, useEffect } from "react";
import { Utility } from "../../../../utils/Utility";
import { DeviceDetailInfoGridProps } from "./DeviceDetailInfoGrid";
import { RadioCell, PingResult } from "../../../terminal/api/TerminalForm";
import { DeviceUiConverter } from "../../../../converter/DeviceUiConverter";
import ByteFormatter from "../../../../utils/formatter/ByteFormatter";

// テキストが省略されているか判定してツールチップを表示するコンポーネント
const EllipsisTooltip: React.FC<{ value: string; color?: string; isBold?: boolean }> = ({ value, color, isBold = false }) => {
  const textRef = useRef<HTMLElement>(null);
  const [isOverflowed, setIsOverflowed] = useState(false);

  useEffect(() => {
    const checkOverflow = () => {
      const element = textRef.current;
      if (element) {
        setIsOverflowed(element.scrollWidth > element.clientWidth);
      }
    };
    checkOverflow();
    window.addEventListener('resize', checkOverflow);
    return () => window.removeEventListener('resize', checkOverflow);
  }, [value]);

  const baseStyle = {
    fontSize: "0.75rem",
    fontWeight: isBold ? 700 : 400,
    fontFamily: "'Noto Sans JP', 'Hiragino Kaku Gothic ProN', 'Meiryo', sans-serif",
    color: color || "#333",
    flex: 1,
    padding: "2px 4px",
    display: 'block',
    borderBottom: 'none',
    whiteSpace: 'nowrap' as const,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  };

  return (
    <Tooltip title={isOverflowed ? value : ''} arrow>
      <Typography ref={textRef} sx={baseStyle}>
        {value}
      </Typography>
    </Tooltip>
  );
};

const style = {
  sectionBox: {
    backgroundColor: "#fff",
    borderRadius: "8px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
    paddingLeft: "24px",
    paddingRight: "24px",
    paddingBottom: "16px",
    paddingTop: "6px",
    marginTop: "-10px",
    marginBottom: "10px",
    display: "flex",
  },
  contentArea: {
    flex: 1,
    paddingLeft: "0px",
    minWidth: 0,
  },
  categoryTitle: {
    fontSize: "1.125rem",
    fontWeight: 600,
    fontFamily: "'Noto Sans JP', 'Hiragino Kaku Gothic ProN', 'Meiryo', sans-serif",
    color: "#333",
    marginBottom: "8px",
  },
  subCategoryTitle: {
    fontSize: "0.875rem",
    backgroundColor: '#e7f2ff',
    px: 1,
    paddingTop: 1,
    paddingBottom: '-1px',
    minHeight: '28px', maxHeight: '28px', height: '28px',
    fontWeight: 550,
    fontFamily: "'Noto Sans JP', 'Hiragino Kaku Gothic ProN', 'Meiryo', sans-serif",
    color: "#333",
    marginBottom: "8px",
  },
  itemBox: {
    paddingTop: "4px",
    paddingBottom: "4px",
    borderBottom: '1px solid #ccc',
    display: 'flex',
    alignItems: 'center',
    gap: '1em',
    minHeight: '28px',
    maxHeight: '28px',
    overflow: 'hidden',
    lineHeight: '20px',
  },
  itemLabel: {
    width: "119px",
    fontSize: "0.8rem",
    fontWeight: 500,
    fontFamily: "'Noto Sans JP', 'Hiragino Kaku Gothic ProN', 'Meiryo', sans-serif",
    color: "#666",
    padding: "2px 8px",
    flexShrink: 0,
    display: 'inline-block',
    borderBottom: 'none',
    justifyContent: 'center',
  },
  itemValue: {
    fontSize: "0.8rem",
    fontWeight: 500,
    fontFamily: "'Noto Sans JP', 'Hiragino Kaku Gothic ProN', 'Meiryo', sans-serif",
    color: "#333",
    flex: 1,
    padding: "2px 0px 2px 8px",
    display: 'inline-block',
    borderBottom: 'none',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    justifyContent: 'center',
  },
  tableCell: {
    padding: '4px 12px',
    borderBottom: '1px solid #ccc',
    minHeight: '28px',
    maxHeight: '28px',
    height: '28px',
  },
};

const DeviceDetailInfoGridForM2U300: React.FC<DeviceDetailInfoGridProps> = ({
  contentColor = "#16191f",
  device,
}) => {
  const radioCells = device.radioSnapshot?.cells ?? [];
  const primaryCell = radioCells.find((cell) => cell.role === "PRIMARY");
  const secondaryCell = radioCells.find((cell) => cell.role === "SECONDARY");
  const sccCells = radioCells.filter((cell) => cell.role === "SCC");
  const networkInterfaces = device.networkInterface ?? [];
  const pingResults = device.ping ?? [];

  const radioValue = (value: number | null | undefined, unit: string) =>
    value == null ? '-' : `${value} ${unit}`;

  // PINGの宛先・到達可否・レイテンシを1行にまとめる
  const pingSummary = (ping: PingResult) => {
    if (ping.status == null) {
      return ping.ip || '-';
    }
    const reachable = ping.status === 0;
    const parts = [ping.ip, reachable ? '到達' : '未到達'];
    if (reachable && ping.latency != null) {
      parts.push(`${ping.latency} ms`);
    }
    return parts.filter((part) => !!part).join(' / ');
  };

  // セルの rat / band / arfcn / pci を1行にまとめる
  const cellSummary = (cell?: RadioCell) => {
    const parts = [
      cell?.rat,
      cell?.band ? `Band ${cell.band}` : undefined,
      cell?.arfcn != null ? `ARFCN ${cell.arfcn}` : undefined,
      cell?.pci != null ? `PCI ${cell.pci}` : undefined,
    ].filter((part): part is string => !!part);
    return parts.length > 0 ? parts.join(' / ') : '-';
  };

  const traffic = (value: string | number) =>
    ByteFormatter.formatBytes({ bytes: (Number(value) || 0) * 1024, unit: "MB", allowHigherUnits: true });

  // 確認済みフラグ: modelNameが存在する場合は登録確認済み
  const isDeviceRegistered = !!(device.modelName && device.modelName.trim() !== '');

  const notRegisteredMessage = (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '24px' }}>
      <Box>
        <Typography sx={{ fontSize: '0.875rem' }}>
          登録確認が行われていないため、表示できません。
        </Typography>
        <Typography sx={{ fontSize: '0.8rem' }}>
          ※該当端末がインターネット接続できる状態にしてください。
        </Typography>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ width: "100%" }}>
      <Grid container spacing={2} sx={{
        flexDirection: { xs: 'column', lg: 'row' },
        '& > .MuiGrid-item': {
          maxWidth: { xs: 'none', lg: '50%' }
        }
      }}>
        {/* 基本情報 */}
        <Grid item lg={6}>
          <Paper sx={style.sectionBox} elevation={0}>
            <Box sx={style.contentArea}>
              <Typography sx={style.categoryTitle}>基本情報</Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography sx={style.subCategoryTitle}>端末情報</Typography>
                  <Box sx={{ ...style.itemBox, marginTop: '-8px' }}>
                    <Typography sx={style.itemLabel}>S/N</Typography>
                    <EllipsisTooltip value={device.serialNumber || '-'} />
                  </Box>
                  <Box sx={style.itemBox}>
                    <Typography sx={style.itemLabel}>IMEI</Typography>
                    <EllipsisTooltip value={device.imei || '-'} />
                  </Box>
                  <Box sx={style.itemBox}>
                    <Typography sx={style.itemLabel}>機種</Typography>
                    <EllipsisTooltip value={isDeviceRegistered ? device.modelName : '-'} />
                  </Box>
                  <Box sx={style.itemBox}>
                    <Typography sx={style.itemLabel}>FW Ver</Typography>
                    <EllipsisTooltip value={device.firmware && device.firmware.trim() !== '' ? device.firmware : '-'} />
                  </Box>
                  <Box sx={style.itemBox}>
                    <Typography sx={style.itemLabel}>登録日時</Typography>
                    <EllipsisTooltip value={device.createDate ? (Utility.dateFormatYmdHis(device.createDate) || '') : '-'} />
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Typography sx={style.subCategoryTitle}>
                    <span style={{ backgroundColor: '#e7f2ff' }}>MDM管理情報</span>
                  </Typography>
                  <Box sx={{ ...style.itemBox, marginTop: '-8px' }}>
                    <Typography sx={style.itemLabel}>MDM管理</Typography>
                    <EllipsisTooltip value={isDeviceRegistered ? (device.isMdmManaged ? "有効" : "無効") : "登録確認未実施"} />
                  </Box>
                  <Box sx={style.itemBox}>
                    <Typography sx={style.itemLabel}>テナント</Typography>
                    <EllipsisTooltip value={device.tenantName || '-'} />
                  </Box>
                  <Box sx={style.itemBox}>
                    <Typography sx={style.itemLabel}>タグ</Typography>
                    <Box sx={{ maxWidth: 240, flex: 1, minWidth: 0 }}>
                      <EllipsisTooltip
                        value={device.tag && device.tag.length > 0 ? device.tag.map((t) => t.tagName).join(", ") : '未設定'}
                        color={device.tag && device.tag.length > 0 ? '#333' : '#bbb'}
                      />
                    </Box>
                  </Box>
                  <Box sx={style.itemBox}>
                    <Typography sx={style.itemLabel}>ラベル</Typography>
                    <Box sx={{ maxWidth: 240, flex: 1, minWidth: 0 }}>
                      <EllipsisTooltip
                        value={device.label && device.label.trim() !== '' ? device.label : '未設定'}
                        color={!device.label ? '#bbb' : '#333'}
                      />
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Paper>

          {/* データ通信量 */}
          <Paper sx={style.sectionBox} elevation={0}>
            <Box sx={style.contentArea}>
              <Typography sx={style.categoryTitle}>データ通信量</Typography>
              {isDeviceRegistered ? (
                <Box sx={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', color: "#333", fontSize: '0.9em' }}>
                    <thead>
                      <tr style={{ ...style.subCategoryTitle, background: '#e7f2ff' }}>
                        <th style={{ padding: '4px 12px', fontWeight: 500, textAlign: 'left', height: '28px' }}>種別</th>
                        <th style={{ padding: '4px 12px', fontWeight: 500, textAlign: 'left' }}>累積</th>
                        <th style={{ padding: '4px 12px', fontWeight: 500, textAlign: 'left' }}>1か月</th>
                        <th style={{ padding: '4px 12px', fontWeight: 500, textAlign: 'left' }}>1日</th>
                      </tr>
                    </thead>
                    <tbody style={{ fontWeight: 400, fontFamily: "'Noto Sans JP', 'Hiragino Kaku Gothic ProN', 'Meiryo', sans-serif", fontSize: '0.9em' }}>
                      <tr>
                        <td style={style.tableCell}>SIM</td>
                        <td style={style.tableCell}>{traffic(device.simTraffic)}</td>
                        <td style={style.tableCell}>{traffic(device.simTraffic1m)}</td>
                        <td style={style.tableCell}>{traffic(device.simTraffic1d)}</td>
                      </tr>
                      <tr>
                        <td style={style.tableCell}>eSIM</td>
                        <td style={style.tableCell}>{traffic(device.esimTraffic)}</td>
                        <td style={style.tableCell}>{traffic(device.esimTraffic1m)}</td>
                        <td style={style.tableCell}>{traffic(device.esimTraffic1d)}</td>
                      </tr>
                    </tbody>
                  </table>
                </Box>
              ) : notRegisteredMessage}
            </Box>
          </Paper>
        </Grid>

        {/* ネットワーク */}
        <Grid item lg={6}>
          <Paper sx={style.sectionBox} elevation={0}>
            <Box sx={style.contentArea}>
              <Typography sx={style.categoryTitle}>ネットワーク</Typography>
              {isDeviceRegistered ? (
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    {/* 通信情報 */}
                    <Typography sx={style.subCategoryTitle}>通信情報</Typography>
                    <Box sx={{ ...style.itemBox, marginTop: '-8px' }}>
                      <Typography sx={style.itemLabel}>事業者</Typography>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <EllipsisTooltip
                          value={device.carrier && device.carrier.trim() !== '' ? device.carrier : '-'}
                          color={DeviceUiConverter.getCarrierColored(device.carrier, contentColor)}
                          isBold
                        />
                      </Box>
                    </Box>
                    <Box sx={style.itemBox}>
                      <Typography sx={style.itemLabel}>オンライン状態</Typography>
                      <EllipsisTooltip value={device.isOnline || '-'} />
                    </Box>
                    <Box sx={style.itemBox}>
                      <Typography sx={style.itemLabel}>IMSI</Typography>
                      <EllipsisTooltip value={device.imsi || '-'} />
                    </Box>
                    <Box sx={style.itemBox}>
                      <Typography sx={style.itemLabel}>MCC</Typography>
                      <EllipsisTooltip value={device.mnetMcc || '-'} />
                    </Box>
                    <Box sx={style.itemBox}>
                      <Typography sx={style.itemLabel}>MNC</Typography>
                      <EllipsisTooltip value={device.mnetMnc || '-'} />
                    </Box>
                    <Box sx={style.itemBox}>
                      <Typography sx={style.itemLabel}>Primary Cell Band</Typography>
                      <EllipsisTooltip value={primaryCell?.band || '-'} />
                    </Box>
                    <Box sx={style.itemBox}>
                      <Typography sx={style.itemLabel}>RSRP</Typography>
                      <EllipsisTooltip value={radioValue(primaryCell?.rsrpDbm, 'dBm')} />
                    </Box>
                    <Box sx={style.itemBox}>
                      <Typography sx={style.itemLabel}>SINR</Typography>
                      <EllipsisTooltip value={radioValue(primaryCell?.sinrDb, 'dB')} />
                    </Box>
                    <Box sx={style.itemBox}>
                      <Typography sx={style.itemLabel}>Secondary RSRP</Typography>
                      <EllipsisTooltip value={radioValue(secondaryCell?.rsrpDbm, 'dBm')} />
                    </Box>
                    <Box sx={style.itemBox}>
                      <Typography sx={style.itemLabel}>Secondary SINR</Typography>
                      <EllipsisTooltip value={radioValue(secondaryCell?.sinrDb, 'dB')} />
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    {/* インターフェース情報 */}
                    <Typography sx={style.subCategoryTitle}>インターフェース情報</Typography>
                    <Box sx={{ ...style.itemBox, marginTop: '-8px' }}>
                      <Typography sx={style.itemLabel}>IP（DHCP）</Typography>
                      <EllipsisTooltip value={device.ipAddress && device.ipAddress.trim() !== '' ? device.ipAddress : '-'} />
                    </Box>
                    <Box sx={style.itemBox}>
                      <Typography sx={style.itemLabel}>WAN IP</Typography>
                      <EllipsisTooltip value={device.wanIp && device.wanIp.trim() !== '' ? device.wanIp : '-'} />
                    </Box>
                    {networkInterfaces.length > 0 ? (
                      networkInterfaces.map((networkInterface, index) => (
                        <Box key={networkInterface.name || `interface-${index}`} sx={style.itemBox}>
                          <Typography sx={style.itemLabel}>{networkInterface.name || `I/F${index + 1}`}</Typography>
                          <EllipsisTooltip value={networkInterface.status || '-'} />
                        </Box>
                      ))
                    ) : (
                      <Box sx={style.itemBox}>
                        <Typography sx={style.itemLabel}>ネットワークI/F</Typography>
                        <EllipsisTooltip value="-" />
                      </Box>
                    )}
                    {pingResults.length > 0 ? (
                      pingResults.map((ping, index) => (
                        <Box key={ping.ip || `ping-${index}`} sx={style.itemBox}>
                          <Typography sx={style.itemLabel}>{`PING${index + 1}`}</Typography>
                          <EllipsisTooltip value={pingSummary(ping)} />
                        </Box>
                      ))
                    ) : (
                      <Box sx={style.itemBox}>
                        <Typography sx={style.itemLabel}>PING</Typography>
                        <EllipsisTooltip value="-" />
                      </Box>
                    )}
                  </Grid>
                  <Grid item xs={12}>
                    {/* セル情報 */}
                    <Typography sx={style.subCategoryTitle}>セル情報</Typography>
                    <Box sx={{ ...style.itemBox, marginTop: '-8px' }}>
                      <Typography sx={style.itemLabel}>取得日時</Typography>
                      <EllipsisTooltip
                        value={device.radioSnapshot?.measuredAt
                          ? (Utility.dateFormatYmdHis(device.radioSnapshot.measuredAt) || '-')
                          : '-'}
                      />
                    </Box>
                    <Box sx={style.itemBox}>
                      <Typography sx={style.itemLabel}>Primary Cell</Typography>
                      <EllipsisTooltip value={cellSummary(primaryCell)} />
                    </Box>
                    <Box sx={style.itemBox}>
                      <Typography sx={style.itemLabel}>Secondary Cell</Typography>
                      <EllipsisTooltip value={cellSummary(secondaryCell)} />
                    </Box>
                    {sccCells.length > 0 ? (
                      sccCells.map((cell, index) => (
                        <Box key={`scc-${cell.carrierIndex ?? index}`} sx={style.itemBox}>
                          <Typography sx={style.itemLabel}>{`SCC${index + 1}`}</Typography>
                          <EllipsisTooltip value={cellSummary(cell)} />
                        </Box>
                      ))
                    ) : (
                      <Box sx={style.itemBox}>
                        <Typography sx={style.itemLabel}>SCC</Typography>
                        <EllipsisTooltip value="-" />
                      </Box>
                    )}
                  </Grid>
                </Grid>
              ) : notRegisteredMessage}
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default DeviceDetailInfoGridForM2U300;
